import { useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router";
import { useShallow } from "zustand/shallow";
import { ArrowCounterClockwiseIcon, TrashSimpleIcon } from "@phosphor-icons/react";

import { Editor } from "@/features/notes/components/Editor";
import { ICON_SIZE } from "@/config/constants";
import { useBoundStore } from "@/app/store";
import { EditorLayout } from "@/features/notes/components/Editor/components/EditorLayout";
import { NoteActions } from "@/features/notes/components/Note/NoteActions";
import { NestedAction } from "@/shared/components/ui/NestedContentNavigator";

// This route should be rendered only on mobile devices
export function NotesTrashNoteRoute() {
  const { deletedNotes, deleteNote, restoreNote } = useBoundStore(
    useShallow((state) => ({
      deletedNotes: state.notes.deleted,
      deleteNote: state.notes.deleteNote,
      restoreNote: state.notes.restoreNote,
    }))
  );
  const { id } = useParams<{ id: string }>();
  const selectedNote = useMemo(() => deletedNotes?.find((note) => note.id === Number(id)), [deletedNotes, id]);
  const navigate = useNavigate();

  const handleClose = () => {
    navigate(-1);
  };

  const actions = useCallback(() => {
    const actions: NestedAction[] = [
      {
        key: "restore",
        label: "Restore",
        icon: <ArrowCounterClockwiseIcon size={ICON_SIZE.md} />,
        run: () => {
          if (selectedNote) {
            restoreNote(selectedNote.id);
          }
          navigate(-2);
        },
      },
      {
        key: "delete",
        label: "Delete forever",
        icon: <TrashSimpleIcon size={ICON_SIZE.md} />,
        run: () => {
          if (selectedNote) {
            deleteNote(selectedNote.id);
          }
          navigate(-2);
        },
      },
    ];

    return actions;
  }, [navigate, selectedNote, deleteNote, restoreNote]);

  if (!selectedNote) {
    return null;
  }

  return (
    <EditorLayout isEditable={false} navbar={{ actions: [<NoteActions actions={actions} />], onBack: handleClose }}>
      <Editor
        initialData={{
          html_content: selectedNote.html_content,
          text_content: selectedNote.text_content,
        }}
        isEditable={false}
        note={selectedNote}
        refetchedNote={selectedNote}
      />
    </EditorLayout>
  );
}
